import { BookOpen } from 'lucide-react';
import type { StudentOverview, StudentProfile } from '@/types/student';

interface AnimatedInstituteHeroProps {
  profile: StudentProfile | null;
  overview: StudentOverview | null;
}

/**
 * The line under the greeting.
 *
 * Both halves are real or neither is said: a child with no register taken yet
 * must not be greeted with an attendance figure, and "3 tasks due this week"
 * when nothing is due is the kind of small lie that costs a panel its
 * credibility with the person who knows their own week best.
 */
function summaryOf(overview: StudentOverview | null) {
  if (!overview) return null;

  const { dueThisWeek } = overview.homework;
  const { percentage } = overview.attendance;

  const work =
    dueThisWeek === 0
      ? 'nothing due this week'
      : `${dueThisWeek} pending homework ${dueThisWeek === 1 ? 'task' : 'tasks'} due this week`;

  return { work, percentage };
}

export function AnimatedInstituteHero({ profile, overview }: AnimatedInstituteHeroProps) {
  const summary = summaryOf(overview);

  return (
    <div className="relative isolate min-h-[200px] sm:min-h-[250px] overflow-hidden rounded-[26px] bg-[linear-gradient(115deg,#2e247b_0%,#4334a6_52%,#312683_100%)] px-5 py-6 text-white shadow-[0_12px_30px_rgba(46,36,123,.25)] sm:px-9 sm:py-8 flex items-center justify-between">
      {/* Left Text Content */}
      <div className="relative z-10 max-w-[460px] flex flex-col gap-2.5 sm:gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-pill border border-white/25 bg-white/[.15] px-3 py-1 text-2xs sm:text-xs font-extrabold shadow-sm backdrop-blur-sm w-fit">
            <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#ffd75d]" /> Academic Session{' '}
            {profile?.academicYear.name ?? '—'}
          </span>
          {/* Compact Class Teacher badge for small mobile screens */}
          {profile?.classTeacher && (
            <span className="inline-flex sm:hidden items-center gap-1 rounded-pill border border-white/20 bg-[#251b68]/80 px-2.5 py-1 text-3xs font-extrabold text-emerald-300">
              CT: {profile.classTeacher}
            </span>
          )}
        </div>

        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white leading-tight">
          Welcome back, <span className="text-[#ffcb32]">{profile?.fullName ?? '—'}! 👋</span>
        </h2>

        {summary && (
          <p className="text-xs sm:text-sm font-medium leading-relaxed text-white/90">
            You have <strong className="text-[#ffcb32]">{summary.work}</strong>
            {summary.percentage !== null && (
              <>
                {' '}
                and your overall attendance is{' '}
                <strong className="text-[#8cffcb]">{summary.percentage}%</strong>
              </>
            )}
            .
          </p>
        )}
      </div>

      {/* Waving 3D Student Character - Anchored Seamlessly to Bottom Right */}
      <img
        src="/student-overview/student-hero.png"
        alt=""
        className="pointer-events-none absolute right-[150px] lg:right-[235px] bottom-0 h-[205px] sm:h-[220px] w-auto object-contain object-bottom z-10 hidden md:block"
      />

      {/* Class Teacher Floating Box */}
      <div className="relative z-20 hidden sm:flex flex-col gap-1 rounded-[20px] border border-white/20 bg-[#251b68]/85 p-4 text-xs shadow-xl backdrop-blur-md w-44 shrink-0 transition-transform hover:scale-105">
        <span className="text-[9px] text-white/70 font-black uppercase tracking-wider">
          CLASS TEACHER
        </span>
        <span className=" text-white text-[13px]">{profile?.classTeacher ?? 'Not allotted yet'}</span>
        {/* There is no rooms table — this is whichever room their week names
            most. A section taught nowhere in particular simply has no line. */}
        {overview?.today.room && (
          <span className="text-2xs text-[#14cba8] font-bold">{overview.today.room}</span>
        )}
        <span className="text-[10px] text-white/70">{profile?.institute.name ?? ''}</span>
      </div>
    </div>
  );
}
